import { useId, useMemo, useState } from 'react'
import { LookupResults } from '@/components/lookup-results'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'

import {
  useOrganizations,
  useCreateOrganization,
  useUpdateOrganization,
  useBulkCreateClubs,
} from '@/api/hooks'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import type { TOrganizationWithShows } from '@schemas'
import { formatDateRangeDateOnly } from '@/lib/date'

const orgFormSchema = z.object({
  name: z.string().min(1, 'Organization name is required'),
  contact: z.string().optional(),
  abbreviation: z.string().optional(),
  website: z.string().optional(),
})

type OrgFormValues = z.infer<typeof orgFormSchema>

export default function OrganizationsPage() {
  const organizationsQuery = useOrganizations({ retry: false })
  const resultsId = useId()
  const createOrganization = useCreateOrganization()
  const updateOrganization = useUpdateOrganization()
  const bulkCreateClubs = useBulkCreateClubs()

  const organizations = useMemo(() => organizationsQuery.data ?? [], [organizationsQuery.data])

  const [createOrgOpen, setCreateOrgOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editingOrg, setEditingOrg] = useState<TOrganizationWithShows | null>(null)

  const createOrgForm = useForm<OrgFormValues>({
    resolver: zodResolver(orgFormSchema),
    defaultValues: { name: '', contact: '', abbreviation: '', website: '' },
  })

  const editOrgForm = useForm<OrgFormValues>({
    resolver: zodResolver(orgFormSchema),
    defaultValues: { name: '', contact: '', abbreviation: '', website: '' },
  })

  const bulkClubForm = useForm<{ names: string }>({
    defaultValues: { names: '' },
  })

  const handleCreateOrganization = async (values: OrgFormValues) => {
    try {
      await createOrganization.mutateAsync({
        name: values.name.trim(),
        contact: values.contact?.trim() || undefined,
        abbreviation: values.abbreviation?.trim() || undefined,
        website: values.website?.trim() || undefined,
      })
      toast.success(`Created organization “${values.name}”`)
      setCreateOrgOpen(false)
      createOrgForm.reset({ name: '', contact: '', abbreviation: '', website: '' })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to create organization'
      toast.error(message)
    }
  }

  const handleOpenEdit = (org: TOrganizationWithShows) => {
    setEditingOrg(org)
    editOrgForm.reset({
      name: org.name,
      contact: org.contact ?? '',
      abbreviation: (org as any).abbreviation ?? '',
      website: (org as any).website ?? '',
    })
    bulkClubForm.reset({ names: '' })
    setEditOpen(true)
  }

  const handleUpdateOrganization = async (values: OrgFormValues) => {
    if (!editingOrg?.id) return
    try {
      await updateOrganization.mutateAsync({
        orgId: editingOrg.id,
        payload: {
          name: values.name.trim(),
          contact: values.contact?.trim() || undefined,
          abbreviation: values.abbreviation?.trim() || undefined,
          website: values.website?.trim() || undefined,
        },
      })
      toast.success(`Updated “${values.name}”`)
      setEditOpen(false)
      setEditingOrg(null)
      editOrgForm.reset({ name: '', contact: '', abbreviation: '', website: '' })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update organization'
      toast.error(message)
    }
  }

  const handleBulkCreateClubs = async (values: { names: string }) => {
    if (!editingOrg?.id) return

    const lines = values.names
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)

    const seen = new Set<string>()
    const uniqueNames: string[] = []

    for (const line of lines) {
      const key = line.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      uniqueNames.push(line)
    }

    if (uniqueNames.length === 0) {
      bulkClubForm.setError('names', {
        type: 'manual',
        message: 'Enter at least one club name.',
      })
      return
    }

    bulkClubForm.clearErrors('names')

    try {
      const result = await bulkCreateClubs.mutateAsync({
        orgId: editingOrg.id,
        names: uniqueNames,
      })

      const createdCount = result.created.length
      const skippedCount = result.skipped?.length ?? 0

      if (createdCount > 0) {
        toast.success(
          `Added ${createdCount} club${createdCount === 1 ? '' : 's'} to ${editingOrg.name}`
        )
      } else if (skippedCount > 0) {
        toast.info('No new clubs added; all names already exist for this organization')
      }

      if (skippedCount > 0) {
        toast.info(`Skipped ${skippedCount} duplicate club${skippedCount === 1 ? '' : 's'}`)
      }

      bulkClubForm.reset({ names: '' })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add clubs'
      toast.error(message)
    }
  }

  return (
    <div className="space-y-8">
      <section className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Organizations</h1>
          <p className="text-muted-foreground">
            Review every host organization, edit contact details, and launch new shows directly from
            the table.
          </p>
        </div>
        <Sheet
          open={createOrgOpen}
          onOpenChange={open => {
            setCreateOrgOpen(open)
            if (!open) {
              createOrgForm.reset({ name: '', contact: '' })
            }
          }}
        >
          <SheetTrigger asChild>
            <Button>Create organization</Button>
          </SheetTrigger>
          <SheetContent side="right">
            <SheetHeader>
              <SheetTitle>New organization</SheetTitle>
              <SheetDescription>Provide details for the hosting organization.</SheetDescription>
            </SheetHeader>
            <div className="px-4">
              <Form {...createOrgForm}>
                <form
                  onSubmit={createOrgForm.handleSubmit(handleCreateOrganization)}
                  className="space-y-4"
                >
                  <FormField
                    control={createOrgForm.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Organization name *</FormLabel>
                        <FormControl>
                          <Input {...field} autoFocus />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={createOrgForm.control}
                    name="contact"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Contact information</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Email or phone" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={createOrgForm.control}
                    name="abbreviation"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Abbreviation</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., CBA" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={createOrgForm.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Website</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., https://example.org" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <SheetFooter>
                    <Button type="submit" disabled={createOrganization.isPending}>
                      {createOrganization.isPending ? 'Creating…' : 'Create organization'}
                    </Button>
                  </SheetFooter>
                </form>
              </Form>
            </div>
          </SheetContent>
        </Sheet>
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Organization directory</CardTitle>
          <CardDescription>Track hosts, contacts, and their show pipelines.</CardDescription>
        </CardHeader>
        <CardContent>
          <LookupResults
            id={resultsId}
            label="Organization directory results"
            query={organizationsQuery}
            loadingMessage="Loading organizations…"
            loadingContent={<OrganizationTableSkeleton />}
            forbiddenMessage="You do not have access to view the organization directory."
            errorMessage="Unable to load organizations. Retry to check the directory."
            successMessage={
              organizations.length === 0
                ? 'No organizations yet. Create one to get started.'
                : `${organizations.length} ${organizations.length === 1 ? 'organization' : 'organizations'} found.`
            }
          >
            {organizations.length > 0 && (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[45%]">Organization</TableHead>
                    <TableHead>Shows</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {organizations.map(org => (
                    <TableRow
                      key={org.id ?? org.name}
                      data-testid={`organization-row-${org.id ?? org.name}`}
                    >
                      <TableCell>
                        <div className="font-medium">{org.name}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium">
                          {org.shows.length} {org.shows.length === 1 ? 'show' : 'shows'}
                        </div>
                        {org.shows.length > 0 ? (
                          <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                            {org.shows.slice(0, 2).map(show => (
                              <li key={show.id ?? show.name}>
                                {show.name} •{' '}
                                {formatDateRangeDateOnly(show.startDate, show.endDate)}
                              </li>
                            ))}
                            {org.shows.length > 2 && <li>+ {org.shows.length - 2} more…</li>}
                          </ul>
                        ) : (
                          <span className="text-xs text-muted-foreground">No shows yet</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => handleOpenEdit(org)}>
                            Edit
                          </Button>
                          <Button size="sm" asChild>
                            <Link to={org.id ? `/shows/new?orgId=${org.id}` : '/shows/new'}>
                              Create show
                            </Link>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </LookupResults>
        </CardContent>
      </Card>

      <Sheet
        open={editOpen}
        onOpenChange={open => {
          setEditOpen(open)
          if (!open) {
            setEditingOrg(null)
            editOrgForm.reset({ name: '', contact: '', abbreviation: '', website: '' })
            bulkClubForm.reset({ names: '' })
          }
        }}
      >
        <SheetContent side="right" className="flex flex-col">
          <SheetHeader>
            <SheetTitle>Edit organization</SheetTitle>
            <SheetDescription>Update organization details.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 space-y-8 overflow-y-auto px-4 pb-6">
            <Form {...editOrgForm}>
              <form
                onSubmit={editOrgForm.handleSubmit(handleUpdateOrganization)}
                className="space-y-4"
              >
                <FormField
                  control={editOrgForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organization name *</FormLabel>
                      <FormControl>
                        <Input {...field} autoFocus />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editOrgForm.control}
                  name="contact"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact information</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Email or phone" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editOrgForm.control}
                  name="abbreviation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Abbreviation</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., CBA" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editOrgForm.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Website</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., https://example.org" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <SheetFooter>
                  <Button type="submit" disabled={updateOrganization.isPending}>
                    {updateOrganization.isPending ? 'Saving…' : 'Save changes'}
                  </Button>
                </SheetFooter>
              </form>
            </Form>

            <div className="border-t border-border/60 pt-6">
              <h3 className="text-sm font-semibold">Bulk add clubs</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Enter one club per line to associate the club directory with this organization.
                Existing clubs are skipped automatically.
              </p>
              <Form {...bulkClubForm}>
                <form
                  onSubmit={bulkClubForm.handleSubmit(handleBulkCreateClubs)}
                  className="mt-4 space-y-3"
                >
                  <FormField
                    control={bulkClubForm.control}
                    name="names"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Club names</FormLabel>
                        <FormControl>
                          <Textarea
                            {...field}
                            rows={6}
                            placeholder={'Example Beekeepers\nValley Apiary Guild'}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end">
                    <Button type="submit" variant="secondary" disabled={bulkCreateClubs.isPending}>
                      {bulkCreateClubs.isPending ? 'Adding…' : 'Add clubs'}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}

function OrganizationTableSkeleton() {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[35%]">
            <Skeleton className="h-4 w-32" />
          </TableHead>
          <TableHead>
            <Skeleton className="h-4 w-24" />
          </TableHead>
          <TableHead>
            <Skeleton className="h-4 w-20" />
          </TableHead>
          <TableHead className="text-right">
            <Skeleton className="ml-auto h-4 w-16" />
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[1, 2, 3].map(key => (
          <TableRow key={key}>
            <TableCell>
              <Skeleton className="h-4 w-40" />
              <Skeleton className="mt-2 h-3 w-24" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-32" />
            </TableCell>
            <TableCell>
              <Skeleton className="h-4 w-20" />
            </TableCell>
            <TableCell>
              <div className="flex justify-end gap-2">
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-8 w-24" />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
